import { AsButton, IconEdit } from '@adserve/adserve-react-components';
import React, { useState, useEffect } from 'react';
import { CampaignGoalEnum } from '../../constants/Enums';
import { DefaultInputStatus } from '../Campaign/CampaignBrief/CampaignBriefUtils';

const btnStyle = {
    width: 'max-width',
    height: 30,
    padding: '0 15px',
    boxSizing: 'border-box',
    fontSize: 15
};

const TopBarCtas = ({
    campaign, // either campaign or campaignBurst
    parentCampaign = undefined,
    isCampaignBurst = false,
    readOnly = false,
    bookedReadOnly = false,
    handleAmendCampaignClick,
    handleCancelCampaignClick,
    handleQualifyOutClick,
    handleDiscardCampaignClick,
    createCampaign,
    handleUpdateWithMessage,
    activeCancellationRequest,
    validCampaignName = false,
    validBurstName = false,
    validCampaignGoal = false,
    validCategory = false,
    validDeal = false,
    validDates,
    showEditButton = false,
    currentCampaignVersionId,
    handleEdit,
    handleClose,
    currentStep,
    externalIsBooked,
    isBurst,
    campaignSaving,
    disableBackButton = false
}) => {
    const [validationResults, setValidationResults] = useState({
        name: DefaultInputStatus(),
        budget: false,
        startDate: false,
        endDate: false,
        campaignId: false
    });

    const validateBudget = (budget, availableBudget) => {
        if (budget && availableBudget) {
            if (budget <= availableBudget) {
                return true;
            }
        }

        return false;
    };

    const validateDates = (startDate, endDate) => {
        const eDate = new Date(endDate);
        const sDate = new Date(startDate);
        if (sDate && sDate <= eDate) {
            return true;
        } else {
            return false;
        }
    };
    const isCreating = campaign.id === window.emptyGuid;
    const isBurstValid = 
        !isCampaignBurst ||
        (campaign?.name && campaign.budget &&
            validationResults.budget &&
            validationResults.startDate &&
            validationResults.endDate &&
            validationResults.campaignId &&
            !validationResults.name?.status?.statusMessage);

    const editDisabled =
        (!readOnly && !(typeof isBurst === 'string' && isBurst === 'true')) ||
        !bookedReadOnly ||
        campaign?.campaignStatusName === 'Qualified Out' ||
        campaign?.campaignStatusName === 'Expired';

    useEffect(() => {
        if (campaign.id !== window.emptyGuid) {
            setValidationResults((validationResults) => {
                return { ...validationResults, campaignId: true };
            });
        }
    }, [campaign.id]);

    useEffect(() => {
        let campaignGoal;
        if (isCampaignBurst && parentCampaign) {
            campaignGoal = parentCampaign.campaignGoal;
        } else {
            campaignGoal = campaign.campaignGoal
        }

        if (campaign.budget && campaignGoal) {
            setValidationResults((validationResults) => {
                return {
                    ...validationResults,
                    budget: validateBudget(
                        campaign.budget,
                        campaignGoal.find(
                            (s) => s.goal.toLowerCase() === CampaignGoalEnum.BUDGET.toLowerCase()
                        ).value
                    )
                };
            });
        } else {
            setValidationResults((validationResults) => {
                return {
                    ...validationResults,
                    budget: false
                };
            });
        }
    }, [campaign.budget, campaign.campaignGoal]);

    useEffect(() => {
        if (campaign.startDate && campaign.endDate) {
            const result = validateDates(campaign.startDate, campaign.endDate);
            setValidationResults((validationResults) => {
                return { ...validationResults, startDate: result, endDate: result };
            });
        } else { 
            setValidationResults((validationResults) => {
                return { ...validationResults, startDate: false, endDate: false };
            });
        }
    }, [campaign.startDate, campaign.endDate]);

    const isBookedCampaign = () => {
        return (
            campaign?.id &&
            campaign?.id !== window.emptyGuid &&
            (campaign?.campaignStatusName === 'Booked' || campaign?.campaignStatusName === 'Running')
        );
    };

    const isDraftCampaign = () =>
        campaign?.campaignStatusName !== 'Booked' &&
        campaign?.campaignStatusName !== 'Qualified Out' &&
        campaign?.campaignStatusName !== 'Running' &&
        campaign?.campaignStatusName !== 'Cancelled' &&
        campaign?.campaignStatusName !== 'Completed' &&
        campaign?.versionNumber === 0;

    const isAmendedCampaign = () =>
        campaign?.id &&
        campaign?.id !== window.emptyGuid &&
        campaign?.campaignStatusName === 'Draft' &&
        campaign?.versionNumber > 0;

    const isAnyReadOnly = () => {
        let isAnyNotReadonly = bookedReadOnly;
        if (isAnyNotReadonly) isAnyNotReadonly = readOnly;
        return isAnyNotReadonly;
    };

    const onSaveClick = () => {
        if (isCreating) {
            createCampaign();
        } else {
            handleUpdateWithMessage();
        }
    };

    const hasActiveCancellationRequest =
        typeof activeCancellationRequest === 'object' && 'id' in activeCancellationRequest;

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: 50,
                gap: 20
            }}
        >
            <span
                style={{
                    fontSize: 22,
                    fontWeight: 500,
                    color: '#414141',
                    textAlign: 'right'
                }}
            >
                {window.translate('Status')} : {campaign.campaignStatusName}
            </span>

            {isBookedCampaign() ? (
                <>
                    <AsButton
                        id={'amend-campaign-button'}
                        style={btnStyle}
                        disabled={bookedReadOnly}
                        handleClick={handleAmendCampaignClick}
                    >
                        {window.translate('Amend Campaign')}
                    </AsButton>
                    <AsButton
                        id={'cancel-campaign-button'}
                        style={btnStyle}
                        disabled={bookedReadOnly || hasActiveCancellationRequest}
                        handleClick={handleCancelCampaignClick}
                    >
                        {window.translate(hasActiveCancellationRequest ? 'Cancel Requested' : 'Cancel Campaign')}
                    </AsButton>
                </>
            ) : isDraftCampaign() ? (
                <AsButton
                    id={'qualify-out-button'}
                    style={btnStyle}
                    disabled={readOnly}
                    handleClick={handleQualifyOutClick}
                >
                    {window.translate('Qualify Out')}
                </AsButton>
            ) : null}
            {isAmendedCampaign() && (
                <AsButton
                    id={'discard-amend-button'}
                    style={btnStyle}
                    useSecondaryStyle={true}
                    disabled={readOnly}
                    handleClick={handleDiscardCampaignClick}
                >
                    {window.translate('Discard Amendment')}
                </AsButton>
            )}
            {currentStep === 0 ? (
                <AsButton
                    id='save'
                    style={btnStyle}
                    handleClick={onSaveClick}
                    disabled={
                        campaignSaving || !campaign?.modified || (isCampaignBurst
                            ? (!isBurstValid || !validBurstName)
                            : (isAnyReadOnly() || !validCampaignName || !validDates ))
                    }
                >
                    {window.translate(campaignSaving ? 'Saving' : 'Save')}
                </AsButton>
            ) : (
                <AsButton
                    id='save'
                    style={btnStyle}
                    handleClick={onSaveClick}
                    disabled={
                        (!isCampaignBurst?.id
                            ? !campaign.modified ||
                              isAnyReadOnly() ||
                              !validCampaignName ||
                              !validCampaignGoal ||
                              !validCategory ||
                              !validDeal ||
                              !validDates
                            : !isBurstValid) || campaignSaving
                    }
                >
                    {window.translate(campaignSaving ? 'Saving' : 'Save')}
                </AsButton>
            )}

            {showEditButton && currentCampaignVersionId === window.emptyGuid && (
                <AsButton
                    disabled={editDisabled}
                    handleClick={handleEdit}
                    useSecondaryStyle
                    style={{
                        width: 30,
                        height: 30,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 0
                    }}
                >
                    <IconEdit id='edit' style={{ padding: 0 }} size={19} onMouseOver={null} onMouseOut={null} />
                </AsButton>
            )}

            <AsButton
                id='close'
                style={btnStyle}
                handleClick={handleClose}
                useSecondaryStyle={true}
                disabled={disableBackButton}
            >
                {window.translate('Back')}
            </AsButton>
        </div>
    );
};

export default TopBarCtas;
